import { useRef } from "react";
import {
  getSetupPage,
  isTwoFactorSetupCompleteResponse,
  readCurrentStep,
  responseUrl,
  serializeWizardForm,
  stripWizardGotoStep,
  submitSetupForm,
  tryParseKnoxSession,
} from "../../api/auth/twoFactorClient.ts";

type SetupState = "generator" | "done";

/**
 * Orchestrates setup wizard progression for the login UI.
 * Keeps transient generator-step HTML in memory so token submit uses
 * the same wizard context that produced the QR code.
 */
export function useMfaLoginFlow() {
  const generatorHtmlRef = useRef<string | null>(null);

  const resetSetupState = () => {
    generatorHtmlRef.current = null;
  };

  const prepareSetupQr = async (): Promise<SetupState> => {
    resetSetupState();
    let res = await getSetupPage();
    let html = res.data ?? "";
    let url = responseUrl(res);

    if (isTwoFactorSetupCompleteResponse(url, html) || tryParseKnoxSession(html)) return "done";

    // Bounded loop avoids hanging if backend template/flow changes unexpectedly.
    for (let i = 0; i < 12; i++) {
      const step = readCurrentStep(html, "setup_view");
      if (isTwoFactorSetupCompleteResponse(url, html)) return "done";
      if (step === "generator") {
        generatorHtmlRef.current = html;
        return "generator";
      }
      if (!step) break;

      const body = serializeWizardForm(html, "setup_view");
      stripWizardGotoStep(body);
      res = await submitSetupForm(body);
      html = res.data ?? "";
      url = responseUrl(res);
    }

    if (readCurrentStep(html, "setup_view") === "generator") {
      generatorHtmlRef.current = html;
      return "generator";
    }
    if (isTwoFactorSetupCompleteResponse(url, html)) return "done";
    throw new Error("Could not reach 2FA setup QR step. Try again.");
  };

  const submitGeneratorToken = async (token: string) => {
    // Fallback GET is used only if local generator snapshot is not available.
    const sourceHtml = generatorHtmlRef.current ?? (await getSetupPage()).data ?? "";
    const body = serializeWizardForm(sourceHtml, "setup_view");
    stripWizardGotoStep(body);
    body.set("setup_view-current_step", "generator");
    body.set("generator-token", token.replace(/\s/g, ""));
    const res = await submitSetupForm(body);
    const raw = res.data ?? "";
    if (isTwoFactorSetupCompleteResponse(responseUrl(res), raw)) {
      generatorHtmlRef.current = null;
    } else if (readCurrentStep(raw, "setup_view") === "generator") {
      generatorHtmlRef.current = raw;
    }
    return res;
  };

  return { prepareSetupQr, submitGeneratorToken, resetSetupState };
}
